import { PatientLayout } from "@/components/layout/patient-layout";
import { useCreateOpenaiConversation, useGetOpenaiConversation } from "@workspace/api-client-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Send,
  Loader2,
  AlertTriangle,
  Phone,
  Thermometer,
  Activity,
  Package,
  ArrowLeft,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { getGetOpenaiConversationQueryKey } from "@workspace/api-client-react";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";

type TriageMode = "symptom-triage" | "flare-assessment" | "pain-management" | "supply-query";

interface ModeConfig {
  id: TriageMode;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
  iconBg: string;
  chatTitle: string;
  chatSubtitle: string;
  placeholder: string;
  conversationTitle: string;
}

const MODES: ModeConfig[] = [
  {
    id: "symptom-triage",
    label: "Symptom Check",
    description: "Describe your symptoms and get guidance on whether to call 999, NHS 111, your GP, or your nurse advice line.",
    icon: Activity,
    color: "text-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    iconBg: "bg-red-100",
    chatTitle: "Symptom Check",
    chatSubtitle: "Tell me your symptoms and I'll help you find the right level of care.",
    placeholder: "Describe how you are feeling right now...",
    conversationTitle: "Symptom Check",
  },
  {
    id: "flare-assessment",
    label: "Flare Assessment",
    description: "Having a flare of your condition? Work through a structured check to understand how to manage it at home and when to seek help.",
    icon: Thermometer,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    iconBg: "bg-orange-100",
    chatTitle: "Flare Assessment",
    chatSubtitle: "Let's assess your flare together and work out the best plan for you.",
    placeholder: "Tell me about your flare — how long, which joints, pain level...",
    conversationTitle: "Flare Assessment",
  },
  {
    id: "pain-management",
    label: "Pain Support",
    description: "Get practical, evidence-based guidance on managing your pain day to day — what works, pacing, heat and cold, exercise, and more.",
    icon: ShieldCheck,
    color: "text-teal-600",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    iconBg: "bg-teal-100",
    chatTitle: "Pain Support",
    chatSubtitle: "Ask anything about managing your pain. I'll share what the evidence says works.",
    placeholder: "What would you like to know about managing your pain?",
    conversationTitle: "Pain Support",
  },
  {
    id: "supply-query",
    label: "Medication Supply",
    description: "Questions about your homecare medication delivery? Get contact details for supply companies and guidance on delivery issues.",
    icon: Package,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    iconBg: "bg-blue-100",
    chatTitle: "Medication Supply",
    chatSubtitle: "I can help with delivery questions and homecare provider contact details.",
    placeholder: "What is your supply or delivery question?",
    conversationTitle: "Medication Supply Query",
  },
];

export default function PatientTriage() {
  const [selectedMode, setSelectedMode] = useState<TriageMode | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [sendError, setSendError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const createConvo = useCreateOpenaiConversation();
  const { data: activeConvo, isLoading: chatLoading } = useGetOpenaiConversation(
    activeId!,
    { query: { enabled: !!activeId, queryKey: getGetOpenaiConversationQueryKey(activeId!) } }
  );

  const mode = MODES.find((m) => m.id === selectedMode) ?? null;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeConvo?.messages, isStreaming]);

  const handleSelectMode = (modeId: TriageMode) => {
    setSelectedMode(modeId);
    const cfg = MODES.find((m) => m.id === modeId)!;
    createConvo.mutate(
      { data: { title: cfg.conversationTitle, userRole: "patient" } },
      {
        onSuccess: (newConvo) => {
          setActiveId(newConvo.id);
        },
      }
    );
  };

  const handleBack = () => {
    setSelectedMode(null);
    setActiveId(null);
    setInput("");
    setIsStreaming(false);
    setRateLimited(false);
    setSendError(false);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !activeId || isStreaming || !selectedMode) return;

    const userMessage = input.trim();
    setInput("");
    setIsStreaming(true);
    setRateLimited(false);
    setSendError(false);

    const tempUserMsg = {
      id: Date.now(),
      conversationId: activeId,
      role: "user",
      content: userMessage,
      createdAt: new Date().toISOString(),
    };
    const tempAssistantMsg = {
      id: Date.now() + 1,
      conversationId: activeId,
      role: "assistant",
      content: "",
      createdAt: new Date().toISOString(),
    };

    queryClient.setQueryData(getGetOpenaiConversationQueryKey(activeId), (old: any) => {
      if (!old) return old;
      return { ...old, messages: [...old.messages, tempUserMsg, tempAssistantMsg] };
    });

    try {
      const response = await fetch(`/api/openai/conversations/${activeId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: userMessage,
          userRole: "patient",
          triageMode: selectedMode,
        }),
      });

      if (response.status === 429) {
        setRateLimited(true);
        queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
        return;
      }

      if (!response.body) throw new Error("No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6);
            if (!dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (data.done) break;
              if (data.content) {
                assistantContent += data.content;
                queryClient.setQueryData(
                  getGetOpenaiConversationQueryKey(activeId),
                  (old: any) => {
                    if (!old) return old;
                    const msgs = [...old.messages];
                    msgs[msgs.length - 1].content = assistantContent;
                    return { ...old, messages: msgs };
                  }
                );
              }
            } catch (_e) {}
          }
        }
      }
    } catch (_error) {
      setSendError(true);
      queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
    } finally {
      setIsStreaming(false);
      queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
    }
  };

  return (
    <PatientLayout title="Symptom & Triage Support">
      {/* Emergency banner — always visible */}
      <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-800">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
        <p className="leading-relaxed">
          <strong>Medical emergency?</strong> Call{" "}
          <a href="tel:999" className="font-bold underline hover:text-red-900">999</a> immediately or go to A&E.
          For urgent advice, call{" "}
          <a href="tel:111" className="font-bold underline hover:text-red-900">NHS 111</a> (free, 24/7).
        </p>
      </div>

      {!selectedMode ? (
        /* Mode selection */
        <div className="space-y-6">
          <div className="max-w-2xl">
            <p className="text-slate-600 text-base leading-relaxed">
              Choose what you need help with. Our AI support tools are here to guide you — they don't replace your healthcare team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => handleSelectMode(m.id)}
                  disabled={createConvo.isPending}
                  className={`text-left rounded-3xl p-6 border ${m.bgColor} ${m.borderColor} hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-400 disabled:opacity-60`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`h-12 w-12 rounded-2xl ${m.iconBg} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${m.color}`} />
                    </div>
                    <ChevronRight className={`h-5 w-5 ${m.color} opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all mt-1`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{m.label}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{m.description}</p>
                </button>
              );
            })}
          </div>

          {/* Nurse advice line finder */}
          <div className="bg-green-50 border border-green-200 rounded-3xl p-5">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800 text-sm mb-1">Finding your rheumatology nurse advice line</p>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">
                  Your local advice line number is on your clinic letters, appointment card, or hospital discharge summary.
                  If you can't find it, call your hospital's main switchboard and ask for the rheumatology department.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-white border border-green-200 text-green-800 rounded-lg px-3 py-1.5 font-medium">NHS 111 — 111 (urgent, 24/7, free)</span>
                  <span className="bg-white border border-green-200 text-green-800 rounded-lg px-3 py-1.5 font-medium">Emergency — 999</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-600">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
            <p className="leading-relaxed">
              These tools provide general information only. They are not a substitute for advice from your rheumatology team, GP, or emergency services.
              All conversations are automatically deleted after 90 days.
            </p>
          </div>
        </div>
      ) : (
        /* Chat interface */
        <div className="space-y-4">
          {/* Mode header */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-slate-500 hover:text-slate-800 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            {mode && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${mode.bgColor} ${mode.borderColor} border`}>
                <mode.icon className={`h-4 w-4 ${mode.color}`} />
                <span className={`text-sm font-medium ${mode.color}`}>{mode.label}</span>
              </div>
            )}
          </div>

          <div
            className="flex flex-col bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden"
            style={{ height: "calc(100vh - 380px)", minHeight: "400px" }}
          >
            {/* Chat messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              {/* Welcome message */}
              {mode && (!activeConvo?.messages || activeConvo.messages.length === 0) && (
                <div className="flex justify-start gap-3">
                  <div className={`h-10 w-10 rounded-full ${mode.iconBg} border ${mode.borderColor} flex items-center justify-center shrink-0`}>
                    <mode.icon className={`h-5 w-5 ${mode.color}`} />
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm px-5 py-4 bg-white border border-slate-200 shadow-sm">
                    <p className="font-semibold text-slate-800 mb-1">{mode.chatTitle}</p>
                    <p className="text-slate-600 leading-relaxed">{mode.chatSubtitle}</p>
                    {selectedMode === "symptom-triage" && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-xl px-3 py-2">
                        <Phone className="h-3 w-3 shrink-0" />
                        If this is an emergency, stop and call <strong className="ml-1">999</strong>.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {chatLoading && (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
                </div>
              )}

              {activeConvo?.messages?.map((msg, i) => (
                <div
                  key={msg.id || i}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && mode && (
                    <div className={`h-10 w-10 rounded-full ${mode.iconBg} border ${mode.borderColor} flex items-center justify-center shrink-0`}>
                      <mode.icon className={`h-5 w-5 ${mode.color}`} />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-5 py-4 ${
                      msg.role === "user"
                        ? "bg-slate-800 text-white rounded-tr-sm"
                        : "bg-white border border-slate-200 shadow-sm text-slate-800 rounded-tl-sm"
                    }`}
                  >
                    <div className="whitespace-pre-wrap leading-relaxed text-base">
                      {msg.content || (msg.role === "assistant" && isStreaming ? "..." : "")}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input area */}
            <div className="p-4 sm:p-5 bg-white border-t border-slate-100 space-y-2">
              {rateLimited && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  You've reached the message limit (30 per hour). Please try again a little later.
                </div>
              )}
              {sendError && (
                <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Something went wrong sending your message. Please try again. If this continues, call{" "}
                  <a href="tel:111" className="underline font-medium">NHS 111</a> for urgent advice.
                </div>
              )}
              <form onSubmit={handleSend} className="flex gap-3 relative">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={mode?.placeholder ?? "Type or dictate your message..."}
                  className="flex-1 rounded-full h-14 pl-6 pr-24 bg-slate-50 border-slate-200 focus-visible:ring-rose-500 text-base"
                  disabled={isStreaming || !activeId}
                />
                <VoiceInputButton
                  tone="rose"
                  size="md"
                  onTranscript={(chunk) => setInput((prev) => appendVoiceChunk(prev, chunk))}
                  disabled={isStreaming || !activeId}
                  label="Dictate message"
                  className="absolute right-14 top-2"
                />
                <Button
                  type="submit"
                  disabled={!input.trim() || isStreaming || !activeId}
                  className="absolute right-2 top-2 h-10 w-10 rounded-full bg-rose-500 hover:bg-rose-600 p-0"
                >
                  {isStreaming ? (
                    <Loader2 className="h-4 w-4 text-white animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 text-white" />
                  )}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center">
                Not a substitute for professional medical advice. Always follow the guidance of your healthcare team.
              </p>
            </div>
          </div>
        </div>
      )}
    </PatientLayout>
  );
}
